import {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  type SyntheticEvent,
  type ReactElement,
} from 'react';
import {
  Autocomplete as MUIAutocomplete,
  ChipProps as MUIChipProps,
} from '@mui/material';
import { debounce, isEqual, uniqWith } from 'lodash';
import {
  AB3, AR1, AR2, B1, S1, S3,
} from '../../../../theme';
import { CaretIcon, CloseIcon } from '../../../../icons';
import Chip from '../../../Chip';
import TextField from '../../../TextField';
import AutocompletePaper from '../AutocompletePaper';
import AutocompleteScrollableListBox from '../AutocompleteScrollableListBox';
import type {
  IAutocompleteDefaultOption,
  IAutocompleteProps,
  IAutocompletePropsWithArguments,
  TAutocompleteOption,
} from '../../index';
import Spinner from '../../../../animations/Spinner';

type TAsyncResultsCallback = (results: readonly IAutocompleteDefaultOption[]) => void

const WITH_SPINNER_SX = { // preserve space for the Spinner element
  '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root': {
    paddingRight: '65px',
  },
  '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
    paddingRight: '91px',
  },
};

const DEFAULT_CLEAR_ICON = <CloseIcon width={18} height={18} color={AB3} />;
const DEFAULT_CHIP_PROPS: IAutocompleteProps['ChipProps'] = {};

const SPINNER_SX = { marginRight: '4px' };
const SPINNER_ELEMENT = (
  <Spinner key="inputSpinner" color={B1} sx={SPINNER_SX} />
);

interface IBuildSXParams {
  preserveSpaceForSpinner: boolean;
}

// Note: these styles are for size="medium"
const buildSX = ({ preserveSpaceForSpinner }: IBuildSXParams) => ({
  '& .MuiAutocomplete-inputRoot': {
    padding: '8px 24px 8px 16px',
    background: S1,
  },
  '& .MuiOutlinedInput-root': {
    rowGap: '8px',
    '.MuiAutocomplete-input': {
      minHeight: '24px',
      padding: '4px 8px 4px 0',
    },
    '.MuiAutocomplete-input::placeholder': {
      opacity: 1,
      color: AB3,
    },
    '.MuiAutocomplete-endAdornment': {
      display: 'flex',
      alignItems: 'center',
      height: '24px',
      top: 'calc(50% - 12px)',
      right: '20px',
      '.MuiAutocomplete-clearIndicator:not(:last-child)': {
        marginRight: '4px',
      },
    },
    '.MuiChip-root': {
      margin: '0 4px 0 0',
    },
  },
  '& .MuiFormControl-root .MuiOutlinedInput-root': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: S3,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: S3,
    },
    '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: S3,
    },
    '&:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline': {
      borderColor: AB3,
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: AR1,
    },
    '&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: B1,
    },
    '&.Mui-focused.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: B1,
    },
    '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: AR2,
    },
    '& legend': {
      marginLeft: '1px',
    },
  },
  ...(preserveSpaceForSpinner ? WITH_SPINNER_SX : {}),
});

const getPlaceholder = (
  multiple: IAutocompleteProps['multiple'],
  valueToCheck: unknown,
  placeholder: IAutocompleteProps['placeholder'],
): string | undefined => (
  multiple && Array.isArray(valueToCheck) && valueToCheck.length ? '' : placeholder
);

export const getAutocompleteOptionLabel = (option: TAutocompleteOption) => {
  if (typeof option === 'string') {
    return option;
  }
  const { label } = option as IAutocompleteDefaultOption;
  return label;
};

const Autocomplete = forwardRef((props: IAutocompleteProps, ref) => {
  const {
    t,
    value,
    defaultValue,
    options: preOptions,
    dataSourceRequest,
    dataSourceResponseParser,
    debounceDelay = 300,
    multiple,
    filterSelectedOptions: preFilterSelectedOptions = true,
    freeSolo = false,
    forcePopupIcon: preForcePopupIcon,
    autoSelect: preAutoSelect,
    selectOnFocus: preSelectOnFocus,
    clearOnBlur: preClearOnBlur,
    onChange,
    onInputChange,
    onOpen,
    openText = '',
    closeText = '',
    clearText = '',
    asyncModeNoOptionsText = t ? t('autocomplete.typeToSelect') : undefined,
    noOptionsText = t ? t('autocomplete.noOptions') : undefined,
    loadingText = t ? t('autocomplete.loading') : undefined,
    popupIcon: prePopupIcon,
    clearIcon = DEFAULT_CLEAR_ICON,
    getOptionKey,
    optionKeyField,
    getOptionLabel,
    renderTags,
    filterOptions,
    ChipProps = DEFAULT_CHIP_PROPS,
    placeholder: originalPlaceholder = '',
    name,
    label: inputLabel,
    onBlur,
    error,
    helperText,
    disableAutoFill = false,
    disabled,
    autoFocus = false,
    fetchOnEmptyQuery = false,
    strictOptionsCompare = true,
    disableCustomScrollbar = false,
    loadedOptionsCount,
    totalLoadedOptionsCount,
    ...restProps
  } = props;

  // 'isInAsyncMode' - if true, options will be asynchronously loaded on each input change
  const isInAsyncMode = typeof dataSourceRequest !== 'undefined';

  if (typeof preOptions === 'undefined' && !isInAsyncMode && !freeSolo) {
    // eslint-disable-next-line max-len
    throw new Error('Either provide \'options\'/\'dataSourceRequest\' to fetch them or turn on the \'freeSolo\' mode.');
  }
  if (isInAsyncMode && typeof loadingText === 'undefined') {
    throw new Error('Provide \'t\' or \'loadingText\' for proper \'loadingText\' value.');
  }

  if (process.env.NODE_ENV !== 'production') {
    if (getOptionKey && optionKeyField) {
      // eslint-disable-next-line no-console
      console.warn(
        'Both \'getOptionKey\' and \'optionKeyField\' are provided. \'getOptionKey\' will take'
        + ' precedence over \'optionKeyField\'. Consider removing one of them.',
      );
    }
  }

  // if it's not multiple select, it's async + freeSolo modes - do not filter selected options
  const filterSelectedOptions = !multiple && isInAsyncMode && freeSolo
    ? false
    : preFilterSelectedOptions;

  const handleGetOptionKey: IAutocompleteProps['getOptionKey'] = useMemo(
    () => getOptionKey ?? ((option) => {
      if (typeof option === 'string') {
        return option;
      }
      /*
        If all of these - option[optionKeyField], option.id, and option.value - resolve to
        'undefined' -> returning 'undefined' will fall back to the default Autocomplete's
        approach (taking getOptionLabel(option) as a key).
      */
      return (optionKeyField ? option[optionKeyField] : null) ?? option.id ?? option.value;
    }),
    [getOptionKey, optionKeyField],
  );

  const handleGetOptionLabel: IAutocompleteProps['getOptionLabel'] = useMemo(
    () => getOptionLabel ?? getAutocompleteOptionLabel,
    [getOptionLabel],
  );

  const getUniqOptions = useCallback((
    optionsToProcess: readonly TAutocompleteOption[],
  ) => uniqWith<TAutocompleteOption>(
    optionsToProcess,
    (a, b) => {
      const aKey = handleGetOptionKey(a);
      const bKey = handleGetOptionKey(b);
      if (aKey && bKey) {
        return aKey === bKey;
      }
      return handleGetOptionLabel(a) === handleGetOptionLabel(b);
    },
  ), [handleGetOptionLabel, handleGetOptionKey]);

  const [options, setOptions] = useState<readonly TAutocompleteOption[]>(() => {
    if (isInAsyncMode) return [];
    if (preOptions) return getUniqOptions(preOptions);
    return [];
  });
  const [inputValue, setInputValue] = useState('');
  const [areOptionsLoading, setAreOptionsLoading] = useState(false);
  const [placeholder, setPlaceholder] = useState(
    getPlaceholder(multiple, defaultValue ?? value, originalPlaceholder),
  );
  const isDataSourceResponseParserValidated = useRef(false);

  const handleRenderInput = useCallback<IAutocompletePropsWithArguments['renderInput']>(
    (params) => {
      /*
        this will place the Spinner element inside the endAdornment element and will preserve all
        endAdornment's styles
      */
      const updatedAdornmentChildren = [];
      if (areOptionsLoading) {
        updatedAdornmentChildren.push(
          SPINNER_ELEMENT,
        );
      }
      if (isValidElement(params.InputProps.endAdornment)) {
        updatedAdornmentChildren.push(params.InputProps.endAdornment.props.children);
      }
      const clonedEndAdornment = cloneElement(
        (params.InputProps.endAdornment as ReactElement) ?? <div />,
        undefined,
        updatedAdornmentChildren,
      );
      if (disableAutoFill) {
        // eslint-disable-next-line no-param-reassign
        params.inputProps.autoComplete = 'none';
      }
      return (
        <TextField
          {...params}
          name={name}
          label={inputLabel}
          placeholder={placeholder}
          onBlur={onBlur}
          error={error}
          helperText={helperText}
          inputRef={ref}
          InputProps={{
            ...params.InputProps,
            endAdornment: clonedEndAdornment,
          }}
          autoFocus={autoFocus}
        />
      );
    },
    [
      name,
      inputLabel,
      placeholder,
      onBlur,
      error,
      helperText,
      disableAutoFill,
      ref,
      areOptionsLoading,
      autoFocus,
    ],
  );

  const handleRenderTags: IAutocompleteProps['renderTags'] = useMemo(
    () => renderTags ?? ((currentValue, getTagProps) => currentValue.map(
      (option: TAutocompleteOption, index: number) => {
        let { icon } = ChipProps;
        let label;
        if (typeof option === 'string') {
          label = option;
        } else {
          const { icon: optionIcon } = option as IAutocompleteDefaultOption;
          if (optionIcon) {
            icon = optionIcon;
          }
          label = handleGetOptionLabel(option);
        }
        const { onDelete: defaultOnDelete, ...restTagProps } = getTagProps({ index });
        const { onDelete } = ChipProps;
        const handleDelete = onDelete
          ? (event: SyntheticEvent) => onDelete(event, option, index)
          : defaultOnDelete;
        return (
          <Chip
            {...restTagProps}
            onDelete={handleDelete}
            icon={icon}
            label={label}
          />
        );
      },
    )),
    [renderTags, handleGetOptionLabel, ChipProps],
  );

  const forcePopupIcon = useMemo<IAutocompleteProps['forcePopupIcon']>(() => {
    if (preForcePopupIcon) {
      return preForcePopupIcon;
    }
    return freeSolo ? !!options.length : undefined;
  }, [preForcePopupIcon, freeSolo, options]);

  const autoSelect = useMemo<IAutocompleteProps['autoSelect']>(() => {
    if (preAutoSelect) {
      return preAutoSelect;
    }
    return freeSolo ? true : undefined;
  }, [preAutoSelect, freeSolo]);

  const selectOnFocus = useMemo<IAutocompleteProps['selectOnFocus']>(() => {
    if (preSelectOnFocus) {
      return preSelectOnFocus;
    }
    return freeSolo ? true : undefined;
  }, [preSelectOnFocus, freeSolo]);

  const clearOnBlur = useMemo<IAutocompleteProps['clearOnBlur']>(() => {
    if (preClearOnBlur) {
      return preClearOnBlur;
    }
    return freeSolo ? true : undefined;
  }, [preClearOnBlur, freeSolo]);

  const popupIcon = useMemo(() => prePopupIcon ?? (
    <CaretIcon width={18} height={18} color={disabled ? S3 : AB3} />
  ), [prePopupIcon, disabled]);

  const handleChange = useCallback<
    NonNullable<IAutocompleteProps['onChange']>
  >(
    (event, newValue, reason, details) => {
      setPlaceholder(getPlaceholder(multiple, newValue, originalPlaceholder));
      if (onChange) {
        onChange(event, newValue, reason, details);
      }
    },
    [onChange, multiple, originalPlaceholder],
  );

  const handleDataSourceResponseParser: IAutocompleteProps['dataSourceResponseParser'] = useMemo(
    () => {
      // track changes and revalidate 'dataSourceResponseParser'
      isDataSourceResponseParserValidated.current = false;
      return (optionsToParse: any[]) => {
        if (!optionsToParse.length) {
          return [];
        }
        const parsedOptions = dataSourceResponseParser
          ? dataSourceResponseParser(optionsToParse)
          : optionsToParse;
        if (!isDataSourceResponseParserValidated.current && parsedOptions.some(
          (option) => !Object.hasOwn(option as IAutocompleteDefaultOption, 'label'),
        )) {
          throw new Error(
            // eslint-disable-next-line max-len
            'In asynchronous mode, \'Autocomplete\' expects options to have the \'label\' property. '
            // eslint-disable-next-line max-len
            + 'Use \'dataSourceResponseParser\' to parse the response and provide the proper option shape.',
          );
        }
        isDataSourceResponseParserValidated.current = true;
        return parsedOptions as readonly IAutocompleteDefaultOption[];
      };
    },
    [dataSourceResponseParser],
  );

  const debouncedDataSourceRequest = useMemo(() => debounce(
    async (searchCriteria: string, callback: TAsyncResultsCallback) => {
      if (!isInAsyncMode) {
        return;
      }
      const newOptions = await dataSourceRequest(searchCriteria);
      const parsedOptions = handleDataSourceResponseParser(newOptions ?? []);
      callback(parsedOptions);
    },
    debounceDelay,
  ), [isInAsyncMode, debounceDelay, dataSourceRequest, handleDataSourceResponseParser]);

  const handleInputChange = useCallback<
    NonNullable<IAutocompleteProps['onInputChange']>
  >(
    (event, currentInputValue, reason) => {
      if (onInputChange) {
        onInputChange(event, currentInputValue, reason);
      }
      if (!isInAsyncMode) {
        return;
      }
      debouncedDataSourceRequest.cancel();
      setInputValue(currentInputValue);
      if (!currentInputValue && !fetchOnEmptyQuery) {
        setOptions([]);
        setAreOptionsLoading(false);
        return;
      }

      if (reason === 'reset') {
        // prevent extra update of options when an option is selected
        setAreOptionsLoading(false);
        return;
      }

      setAreOptionsLoading(true);
      setOptions([]);
      debouncedDataSourceRequest(currentInputValue, (results) => {
        // for multiple autocomplete, keep selected options in overall options (prevents warning)
        const selectedOptions = multiple && Array.isArray(value) && value.length ? [...value] : [];

        if (filterSelectedOptions || !currentInputValue) {
          // keep selected options at the end so 'uniqWith' can properly keep the actual values
          setOptions(getUniqOptions([...results, ...selectedOptions]));
          setAreOptionsLoading(false);
          return;
        }

        const inputValueToCompare = strictOptionsCompare
          ? currentInputValue
          : currentInputValue.toLowerCase();

        const isInResults = results.some(
          (result) => {
            const optionLabelToCompare = handleGetOptionLabel(result);
            if (strictOptionsCompare) {
              return optionLabelToCompare === inputValueToCompare;
            }
            return optionLabelToCompare.toLowerCase() === inputValueToCompare;
          },
        );

        const newOptions = isInResults || !freeSolo
          ? results
          : [currentInputValue, ...results];
        // keep selected options at the end so 'uniqWith' can properly keep the actual values
        setOptions(getUniqOptions([...newOptions, ...selectedOptions]));

        setAreOptionsLoading(false);
      });
    },
    [
      onInputChange,
      isInAsyncMode,
      debouncedDataSourceRequest,
      fetchOnEmptyQuery,
      multiple,
      value,
      filterSelectedOptions,
      getUniqOptions,
      strictOptionsCompare,
      handleGetOptionLabel,
      freeSolo,
    ],
  );

  const handleOnOpen = useCallback<
    NonNullable<IAutocompleteProps['onOpen']>
  >((event) => {
    if (onOpen) {
      onOpen(event);
    }
    if (!fetchOnEmptyQuery || !!inputValue) {
      return;
    }
    setAreOptionsLoading(true);
    setOptions([]);
    debouncedDataSourceRequest('', (results) => {
      setOptions(getUniqOptions(results));
      setAreOptionsLoading(false);
    });
  }, [onOpen, fetchOnEmptyQuery, inputValue, debouncedDataSourceRequest, getUniqOptions]);

  const handleIsOptionEqualToValue = useCallback<
    NonNullable<IAutocompleteProps['isOptionEqualToValue']>
  >(
    (option, currentValue) => {
      if (typeof currentValue === 'string') {
        const optionLabel = handleGetOptionLabel(option);
        if (strictOptionsCompare) {
          return optionLabel === currentValue;
        }
        return optionLabel.toLowerCase() === currentValue.toLowerCase();
      }
      return isEqual(option, currentValue);
    },
    [handleGetOptionLabel, strictOptionsCompare],
  );

  const handleFilterOptions = useMemo<IAutocompleteProps['filterOptions']>(() => {
    if (filterOptions) {
      return filterOptions;
    }
    if (isInAsyncMode) {
      // do not filter options on async mode
      return (optionsToFilter) => optionsToFilter;
    }
    return undefined;
  }, [filterOptions, isInAsyncMode]);

  useEffect(() => {
    setPlaceholder(getPlaceholder(multiple, defaultValue ?? value, originalPlaceholder));
  }, [multiple, defaultValue, value, originalPlaceholder]);

  useEffect(() => {
    // update options if they were dynamically changed, and it's not async mode
    if (!isInAsyncMode && preOptions) {
      setOptions((prevOptions) => {
        if (isEqual(preOptions, prevOptions)) {
          return prevOptions;
        }
        return getUniqOptions(preOptions);
      });
    }
  }, [isInAsyncMode, preOptions, getUniqOptions]);

  const processedNoOptionsText = useMemo(() => {
    if (!isInAsyncMode || inputValue) {
      /*
       return default text if:
         autocomplete is not in the async mode
         autocomplete is in the async mode, there is an input value but no options are found
      */
      return noOptionsText;
    }
    // return user-friendly text if it's async mode and there is no input value
    return asyncModeNoOptionsText;
  }, [isInAsyncMode, inputValue, noOptionsText, asyncModeNoOptionsText]);

  const ListBoxComponent = useMemo<IAutocompleteProps['ListboxComponent']>(() => (
    disableCustomScrollbar ? undefined : AutocompleteScrollableListBox
  ), [disableCustomScrollbar]);

  const AutocompletePaperComponent = useCallback<
    NonNullable<IAutocompleteProps['PaperComponent']>
  >(
    (autocompletePaperProps) => (
      <AutocompletePaper
        {...autocompletePaperProps}
        t={t}
        loadedOptionsCount={loadedOptionsCount}
        totalLoadedOptionsCount={totalLoadedOptionsCount}
        withLabel={!!inputLabel}
      />
    ),
    [loadedOptionsCount, totalLoadedOptionsCount, inputLabel, t],
  );

  return (
    <MUIAutocomplete
      {...restProps}
      sx={buildSX({ preserveSpaceForSpinner: isInAsyncMode })}
      value={value}
      defaultValue={defaultValue}
      options={options}
      multiple={multiple}
      filterSelectedOptions={filterSelectedOptions}
      freeSolo={freeSolo}
      forcePopupIcon={forcePopupIcon}
      autoSelect={autoSelect}
      selectOnFocus={selectOnFocus}
      clearOnBlur={clearOnBlur}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onOpen={handleOnOpen}
      loading={areOptionsLoading}
      openText={openText}
      closeText={closeText}
      clearText={clearText}
      noOptionsText={processedNoOptionsText}
      loadingText={loadingText}
      popupIcon={popupIcon}
      clearIcon={clearIcon}
      isOptionEqualToValue={handleIsOptionEqualToValue}
      renderInput={handleRenderInput}
      getOptionKey={handleGetOptionKey}
      getOptionLabel={handleGetOptionLabel}
      renderTags={handleRenderTags}
      filterOptions={handleFilterOptions}
      ChipProps={ChipProps as MUIChipProps}
      PaperComponent={AutocompletePaperComponent}
      ListboxComponent={ListBoxComponent}
      disabled={disabled}
    />
  );
});

export default memo(Autocomplete);
